import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, Loader2, ChevronRight, Bell, Flag } from "lucide-react";
import { launchpadApi } from "@/lib/api";
import useRefreshOnFocus from "@/lib/useRefreshOnFocus";
import { initials, colorFor } from "@/lib/avatar";
import { useAuth } from "@/lib/auth";
import NudgeDialog from "@/components/launchpad/NudgeDialog";

// AtRiskWidget — Feb 2026.
//
// Triage queue on Launchpad. Surfaces the top N tasks that are either:
//   - LATE (past due_date and not done), or
//   - DUE SOON (due within next 48h and not done)
//
// Clicking a row deep-links to that task's Plan page with the dialog
// auto-opened (?task=<id>), reusing the same routing trick the Discussion
// page uses.

const PHASE_PILL = {
  pre: "bg-teal-500/15 text-teal-300 border-teal-500/40",
  prod: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  post: "bg-violet-500/15 text-violet-300 border-violet-500/40",
};

export default function AtRiskWidget() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgeTaskId, setNudgeTaskId] = useState(null);

  useRefreshOnFocus(() => {
    let cancelled = false;
    launchpadApi
      .getAtRisk(8)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <article
        data-testid="at-risk-loading"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5 flex items-center gap-2 text-zinc-500 text-sm"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading at-risk tasks…
      </article>
    );
  }

  if (!data) return null;

  const tasks = data.tasks || [];
  const lateCount = data.late_count || 0;
  const dueSoonCount = data.due_soon_count || 0;
  const totalCount = lateCount + dueSoonCount;

  // Empty state: clean inbox-zero moment, briefly celebrating it.
  if (tasks.length === 0) {
    return (
      <article
        data-testid="at-risk-widget"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
        style={{ borderLeftColor: "#10b981", borderLeftWidth: 3 }}
      >
        <div className="text-overline text-emerald-500/80 mb-1 flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" /> At Risk
        </div>
        <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
          Nothing&apos;s late or due in the next 48 hours.
        </h2>
        <p className="text-xs text-zinc-500 mt-1">
          You&apos;re ahead of the curve. Enjoy it while it lasts.
        </p>
      </article>
    );
  }

  return (
    <article
      data-testid="at-risk-widget"
      className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
      style={{
        borderLeftColor: lateCount > 0 ? "#fb7185" : "#fbbf24",
        borderLeftWidth: 3,
      }}
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div
            className={`text-overline mb-1 flex items-center gap-1.5 ${
              lateCount > 0 ? "text-rose-400/80" : "text-amber-400/80"
            }`}
          >
            <AlertTriangle className="w-3 h-3" /> At Risk · triage queue
          </div>
          <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
            {totalCount} task{totalCount === 1 ? "" : "s"} need attention
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            {lateCount > 0 && (
              <span className="text-rose-300">
                {lateCount} late
              </span>
            )}
            {lateCount > 0 && dueSoonCount > 0 && (
              <span className="text-zinc-600"> · </span>
            )}
            {dueSoonCount > 0 && (
              <span className="text-amber-300">
                {dueSoonCount} due in next 48h
              </span>
            )}
          </p>
        </div>
      </div>

      <ul className="space-y-1.5" data-testid="at-risk-list">
        {tasks.map((t) => {
          const phaseClass = PHASE_PILL[t.phase] || PHASE_PILL.pre;
          const isLate = t.status === "late";
          const isMilestone = !!t.is_milestone;
          const offsetLabel = isLate
            ? `${Math.abs(t.days_offset)}d overdue`
            : t.days_offset === 0
            ? "due today"
            : `due in ${t.days_offset}d`;
          const hasAssignee = !!t.assigned_user_id;
          const isSelfAssigned = t.assigned_user_id && t.assigned_user_id === user?.id;
          const canNudge = hasAssignee && !isSelfAssigned;
          return (
            <li
              key={t.task_id}
              data-is-milestone={isMilestone ? "true" : undefined}
              className={`group flex items-center rounded-sm transition-colors ${
                isMilestone
                  ? "hover:bg-violet-950/40 bg-violet-950/15 border border-violet-500/25"
                  : "hover:bg-zinc-900/60"
              }`}
            >
              <button
                type="button"
                data-testid={`at-risk-row-${t.task_id}`}
                onClick={() =>
                  navigate(`/projects/${t.project_id}/plan?task=${t.task_id}`)
                }
                className="flex-1 min-w-0 text-left flex items-center gap-3 py-2 pl-2 pr-1"
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    isLate ? "bg-rose-400" : "bg-amber-400"
                  }`}
                />
                {isMilestone ? (
                  <span
                    data-testid={`at-risk-milestone-badge-${t.task_id}`}
                    className="px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border shrink-0 bg-violet-500/25 text-violet-100 border-violet-500/60 inline-flex items-center gap-1 font-bold"
                    title="Milestone"
                  >
                    <Flag className="w-2.5 h-2.5" />
                    Milestone
                  </span>
                ) : (
                  <span
                    className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border shrink-0 ${phaseClass}`}
                  >
                    {t.phase || "—"}
                  </span>
                )}
                <span
                  className={`flex-1 text-sm truncate min-w-0 ${
                    isMilestone ? "text-violet-100 font-medium" : "text-zinc-200"
                  }`}
                >
                  {t.task_name}
                </span>
                {t.assigned_user_id && (
                  <span
                    data-testid={`at-risk-assignee-${t.task_id}`}
                    className="hidden md:inline-flex items-center gap-1.5 text-[11px] text-zinc-300 max-w-[140px] shrink-0"
                    title={`Assigned to ${t.assigned_user_name || "—"}`}
                  >
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-mono font-bold text-zinc-950 shrink-0"
                      style={{ backgroundColor: colorFor(t.assigned_user_id) }}
                    >
                      {initials(t.assigned_user_name || "?")}
                    </span>
                    <span className="truncate">
                      {(t.assigned_user_name || "").split(" ")[0] || "—"}
                    </span>
                  </span>
                )}
                <span className="text-[10px] text-zinc-500 font-mono truncate hidden sm:inline max-w-[160px]">
                  {t.project_name}
                </span>
                <span
                  className={`text-[10px] font-mono uppercase tracking-wide flex items-center gap-1 shrink-0 ${
                    isLate ? "text-rose-300" : "text-amber-300"
                  }`}
                >
                  <Clock className="w-3 h-3" /> {offsetLabel}
                </span>
              </button>
              {isSelfAssigned ? (
                <span
                  data-testid={`at-risk-its-you-${t.task_id}`}
                  title="This one's on your plate — no nudge needed"
                  className="mx-1 inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-wide border border-emerald-800/60 bg-emerald-950/30 text-emerald-300 shrink-0"
                >
                  <span aria-hidden="true">✓</span>
                  It&apos;s you
                </span>
              ) : (
                <button
                  type="button"
                  data-testid={`at-risk-nudge-${t.task_id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canNudge) return;
                    setNudgeTaskId(t.task_id);
                  }}
                  disabled={!canNudge}
                  title={
                    !hasAssignee
                      ? "Assign someone first"
                      : `Nudge ${t.assigned_user_name || "the assignee"}`
                  }
                  className={`mx-1 inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-wide border transition-colors shrink-0 ${
                    canNudge
                      ? "border-zinc-700 text-zinc-300 hover:border-amber-500/60 hover:text-amber-400 hover:bg-amber-500/10"
                      : "border-zinc-800 text-zinc-600 cursor-not-allowed"
                  }`}
                >
                  <Bell className="w-3 h-3" />
                  Nudge
                </button>
              )}
              <ChevronRight
                className="w-3.5 h-3.5 text-zinc-700 group-hover:text-amber-400 transition-colors shrink-0 mr-2 pointer-events-none"
              />
            </li>
          );
        })}
      </ul>
      <NudgeDialog
        taskId={nudgeTaskId}
        open={!!nudgeTaskId}
        onOpenChange={(o) => !o && setNudgeTaskId(null)}
      />
    </article>
  );
}
